import { createRouter, createWebHistory } from "vue-router";
import routes, { navigationGuard } from "./routes";
import { trackPageView } from "@/utils/analytics";
import { routeRecordIsProtected } from "./routeMeta";

// configure router
const router = createRouter({
  history: createWebHistory(),
  routes, // short for routes: routes
  linkActiveClass: "active",
  scrollBehavior(to, from, savedPosition) {
    const isPrivateRoute = to.matched.some((record) => routeRecordIsProtected(record));
    // Header sticky + respiration : évite que les ancres soient cachées
    const HEADER_OFFSET = 96;

    // Si l'utilisateur utilise les boutons précédent/suivant du navigateur
    if (savedPosition) {
      // Sur les zones privées (tables/dashboards), garder un scroll immédiat (moins "janky")
      return isPrivateRoute ? savedPosition : { ...savedPosition, behavior: 'smooth' };
    }
    // Si une ancre (#section) est présente, scroller vers l'élément
    if (to.hash) {
      return {
        el: to.hash,
        top: HEADER_OFFSET,
        behavior: 'smooth',
      };
    }
    // Sinon, toujours remonter en haut à chaque navigation
    return isPrivateRoute
      ? { top: 0, left: 0 }
      : { top: 0, left: 0, behavior: 'smooth' };
  },
});

// 1. Un bouton "Déconnexion" qui supprime le token et redirige vers la page d'acceuil.
// 2. Un beforeEach dans le router qui empêche d'accéder aux pages protégées après déconnexion/avant connexion.

// Activer le guard de navigation centralisé
router.beforeEach(navigationGuard);

const SEO_DEFAULTS = {
  siteName: "patrimonea by Gwem",
  title: "patrimonea by Gwem | Gestion de patrimoine",
  description:
    "patrimonea by Gwem accompagne particuliers et familles dans l'analyse, l'organisation et le suivi de leur patrimoine.",
  canonicalHost: "test.patrimoneabygwem.com",
};

function upsertMetaTag(attribute, value, content) {
  if (typeof document === "undefined" || !content) return;
  let tag = document.head.querySelector(`meta[${attribute}="${value}"]`);
  if (!tag) {
    tag = document.createElement("meta");
    tag.setAttribute(attribute, value);
    document.head.appendChild(tag);
  }
  tag.setAttribute("content", content);
}

function upsertLinkTag(rel, href) {
  if (typeof document === "undefined" || !href) return;
  let tag = document.head.querySelector(`link[rel="${rel}"]`);
  if (!tag) {
    tag = document.createElement("link");
    tag.setAttribute("rel", rel);
    document.head.appendChild(tag);
  }
  tag.setAttribute("href", href);
}

function buildCanonicalUrl(fullPath) {
  if (typeof window === "undefined") return "";
  const productionHosts = new Set(["patrimoneabygwem.com", "test.patrimoneabygwem.com"]);
  const origin = productionHosts.has(window.location.hostname)
    ? `https://${SEO_DEFAULTS.canonicalHost}`
    : window.location.origin;
  return `${origin}${fullPath || "/"}`;
}

router.afterEach((to) => {
  if (typeof document === "undefined") return;

  const pageTitle = to.meta?.title || to.name || SEO_DEFAULTS.siteName;
  document.title =
    pageTitle === SEO_DEFAULTS.title || pageTitle === SEO_DEFAULTS.siteName
      ? SEO_DEFAULTS.title
      : `${pageTitle} | ${SEO_DEFAULTS.siteName}`;

  const description = to.meta?.description || SEO_DEFAULTS.description;
  const isPrivateRoute = to.matched.some((record) => routeRecordIsProtected(record));
  // Marqueur lu par tokens.css : les titres du site vitrine sont en Muli.
  document.body?.classList.toggle("is-public-page", !isPrivateRoute);
  const robots = to.meta?.robots || (isPrivateRoute ? "noindex, nofollow" : "index, follow");
  const canonicalUrl = buildCanonicalUrl(to.fullPath);

  upsertMetaTag("name", "description", description);
  upsertMetaTag("name", "robots", robots);
  upsertMetaTag("property", "og:title", document.title);
  upsertMetaTag("property", "og:description", description);
  upsertMetaTag("property", "og:url", canonicalUrl);
  upsertMetaTag("name", "twitter:title", document.title);
  upsertMetaTag("name", "twitter:description", description);
  upsertLinkTag("canonical", canonicalUrl);

  trackPageView({
    path: to.fullPath,
    title: document.title,
    location: canonicalUrl,
  });
});

// Tracer les erreurs de navigation et gérer ChunkLoadError (ancien chunk après déploiement)
router.onError((err) => {
  console.error('[Router error]', err);
  const isChunkLoadError = err?.name === 'ChunkLoadError' || (err?.message && /Loading chunk \d+ failed|ChunkLoadError/i.test(err.message));
  if (isChunkLoadError) {
    if (typeof window !== 'undefined' && !window.__chunkReloadAttempted) {
      window.__chunkReloadAttempted = true;
      window.location.reload();
      return;
    }
  }
  throw err;
});

export default router;
